// src/components/AIInsights.jsx
import React, { useEffect, useRef, useState } from 'react';
import { BrainCircuit } from 'lucide-react';
import { callGeminiProxy } from '../lib/gemini';
import { formatCurrency } from '../lib/constants';

export default function AIInsights({ totalInflows, totalOutflows, balance, expensesByCategory }) {
  const [insights, setInsights]   = useState('');
  const [loading, setLoading]     = useState(false);

  // Tracks the active countdown interval so it can be cleared on re-click or unmount.
  const countdownRef = useRef(null);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const generate = async () => {
    // Cancel any running countdown before starting a new request.
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    setLoading(true);
    setInsights('');

    const data = {
      totalInflows,
      totalOutflows,
      balance,
      topExpenses: expensesByCategory.slice(0, 5).map(([cat, amt]) => ({
        category: cat,
        amount: formatCurrency(amt),
      })),
    };

    const prompt = `Project Financial Data: ${JSON.stringify(data)}.
Act as a CFA analyst. Provide:
1. A 2-paragraph summary of this project's financial health and cash flow position.
2. Two specific, actionable recommendations to improve cost efficiency.
Use HTML tags (<b>, <br/>, <ul>, <li>). Do NOT use markdown. Be concise and direct.`;

    try {
      const result = await callGeminiProxy(prompt);
      setInsights(result);
    } catch (err) {
      const countdownSeconds = err.status === 429 ? 60 : err.status === 503 ? 15 : 0;
      const baseMsg =
        err.status === 429
          ? 'Rate limit reached.'
          : err.status === 503
          ? 'Service temporarily unavailable.'
          : err.message || 'Unable to generate insights. Please try again.';

      if (countdownSeconds > 0) {
        let remaining = countdownSeconds;
        const tick = () => {
          setInsights(
            `<em>${baseMsg} Please wait ${remaining}s before retrying.</em>`,
          );
          if (remaining <= 0) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            setInsights(
              `<em>${baseMsg} You can now click "Analyze Ledger" to try again.</em>`,
            );
          }
          remaining -= 1;
        };
        tick();
        countdownRef.current = setInterval(tick, 1000);
      } else {
        setInsights(`<em>${baseMsg}</em>`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#1e1b4b] p-6 rounded-2xl shadow-sm text-white relative overflow-hidden">
      <div className="absolute right-0 top-0 w-64 h-64 bg-indigo-600 rounded-full blur-[100px] opacity-30 pointer-events-none" />

      <div className="flex justify-between items-center mb-5 relative z-10">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <BrainCircuit className="w-5 h-5 text-amber-400" /> AI Financial Insights
        </h2>
        <button
          onClick={generate}
          disabled={loading}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 flex items-center gap-2 border border-white/10"
        >
          ✨ {loading ? 'Analyzing...' : 'Analyze Ledger'}
        </button>
      </div>

      <div className="relative z-10">
        {insights ? (
          <div
            className="text-sm text-indigo-100/90 space-y-3 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: insights }}
          />
        ) : (
          <p className="text-sm text-indigo-200/60 leading-relaxed">
            Click "Analyze Ledger" to have your AI CFA review the project's financial health,
            summarize cash flow, and recommend cost-saving actions based on your categorized expenses.
          </p>
        )}
      </div>
    </div>
  );
}
